"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowRight, Bandage, Braces, Brain, ChevronDown, ClipboardList, Columns3Cog, Database, DollarSign, Eye, Globe, Image, Key, Link as LinkIcon, MessageCircle, Route, Shield, Video, Zap } from "lucide-react";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import { useDocsI18n, type DocsLocale } from "@/lib/i18n";
import type { ReactNode } from "react";
interface Feature {
  title: Record<DocsLocale, string>;
  description: Record<DocsLocale, string>;
  href: string;
  icon: ReactNode;
}
const features: Feature[] = [
// Core gateway value
{
  title: {
    en: "Routing",
    "zh-CN": "智能路由"
  },
  description: {
    en: "Intelligently route requests to the best available models and providers.",
    "zh-CN": "将请求智能路由到最合适的模型和提供商。"
  },
  href: "/features/routing",
  icon: <Route className="size-5" />
}, {
  title: {
    en: "Caching",
    "zh-CN": "缓存"
  },
  description: {
    en: "Reduce costs and latency by caching identical requests.",
    "zh-CN": "缓存相同请求，降低成本和延迟。"
  },
  href: "/features/caching",
  icon: <Zap className="size-5" />
}, {
  title: {
    en: "Image Generation",
    "zh-CN": "图像生成"
  },
  description: {
    en: "Generate images using AI models through the OpenAI-compatible API.",
    "zh-CN": "通过 OpenAI 兼容 API 调用 AI 模型生成图像。"
  },
  href: "/features/image-generation",
  icon: <Image className="size-5" />
}, {
  title: {
    en: "Video Generation",
    "zh-CN": "视频生成"
  },
  description: {
    en: "Generate videos using the OpenAI-compatible async video API.",
    "zh-CN": "通过 OpenAI 兼容异步视频 API 生成视频。"
  },
  href: "/features/video-generation",
  icon: <Video className="size-5" />
},
// Model capabilities
{
  title: {
    en: "Web Search",
    "zh-CN": "网页搜索"
  },
  description: {
    en: "Enable real-time web search capabilities for up-to-date information.",
    "zh-CN": "启用实时网页搜索能力，获取最新信息。"
  },
  href: "/features/web-search",
  icon: <Globe className="size-5" />
}, {
  title: {
    en: "Reasoning",
    "zh-CN": "推理"
  },
  description: {
    en: "Use reasoning-capable models that show their step-by-step thought process.",
    "zh-CN": "使用支持推理的模型，并获取可观测的推理过程。"
  },
  href: "/features/reasoning",
  icon: <Brain className="size-5" />
}, {
  title: {
    en: "Vision",
    "zh-CN": "视觉"
  },
  description: {
    en: "Send images to vision-enabled models using URLs or inline base64 data.",
    "zh-CN": "通过 URL 或内联 base64 数据向视觉模型发送图片。"
  },
  href: "/features/vision",
  icon: <Eye className="size-5" />
},
// Operations & management
{
  title: {
    en: "Cost Breakdown",
    "zh-CN": "成本明细"
  },
  description: {
    en: "Get real-time cost information for each API request directly in the response.",
    "zh-CN": "直接在响应中获取每次 API 请求的实时成本信息。"
  },
  href: "/features/cost-breakdown",
  icon: <DollarSign className="size-5" />
}, {
  title: {
    en: "API Keys & IAM",
    "zh-CN": "API Key 与 IAM"
  },
  description: {
    en: "Manage API keys with fine-grained access control and IAM rules.",
    "zh-CN": "通过细粒度访问控制和 IAM 规则管理 API key。"
  },
  href: "/features/api-keys",
  icon: <Key className="size-5" />
}, {
  title: {
    en: "Guardrails",
    "zh-CN": "护栏"
  },
  description: {
    en: "Protect your LLM usage with content guardrails that detect and block harmful content.",
    "zh-CN": "用内容护栏检测并阻止有害内容，保护 LLM 使用流程。"
  },
  href: "/features/guardrails",
  icon: <Shield className="size-5" />
}, {
  title: {
    en: "Audit Logs",
    "zh-CN": "审计日志"
  },
  description: {
    en: "Track all organization activity with comprehensive audit logs.",
    "zh-CN": "通过完整审计日志追踪组织内所有活动。"
  },
  href: "/features/audit-logs",
  icon: <ClipboardList className="size-5" />
},
// Developer experience & compatibility
{
  title: {
    en: "Custom Providers",
    "zh-CN": "自定义提供商"
  },
  description: {
    en: "Integrate custom OpenAI-compatible providers for enhanced flexibility.",
    "zh-CN": "接入自定义 OpenAI 兼容提供商，获得更高灵活性。"
  },
  href: "/features/custom-providers",
  icon: <Columns3Cog className="size-5" />
}, {
  title: {
    en: "Anthropic API",
    "zh-CN": "Anthropic API"
  },
  description: {
    en: "Access any model through the familiar Anthropic API format.",
    "zh-CN": "通过熟悉的 Anthropic API 格式访问任意模型。"
  },
  href: "/features/anthropic-endpoint",
  icon: <MessageCircle className="size-5" />
}, {
  title: {
    en: "Response Healing",
    "zh-CN": "响应修复"
  },
  description: {
    en: "Automatically repair malformed JSON responses from AI models.",
    "zh-CN": "自动修复 AI 模型返回的格式错误 JSON 响应。"
  },
  href: "/features/response-healing",
  icon: <Bandage className="size-5" />
}, {
  title: {
    en: "Data Retention",
    "zh-CN": "数据保留"
  },
  description: {
    en: "Store and access your full request and response data for debugging and analytics.",
    "zh-CN": "保存并访问完整请求和响应数据，用于调试与分析。"
  },
  href: "/features/data-retention",
  icon: <Database className="size-5" />
}, {
  title: {
    en: "Source Attribution",
    "zh-CN": "来源归因"
  },
  description: {
    en: "Use the X-Source header to identify your domain for public usage statistics.",
    "zh-CN": "使用 X-Source header 标识你的域名，用于公开使用统计。"
  },
  href: "/features/source",
  icon: <LinkIcon className="size-5" />
}, {
  title: {
    en: "Metadata",
    "zh-CN": "元数据"
  },
  description: {
    en: "Send additional context and metadata to doteb using custom headers.",
    "zh-CN": "通过自定义 header 向 doteb 发送额外上下文和元数据。"
  },
  href: "/features/metadata",
  icon: <Braces className="size-5" />
}];
const INITIAL_COUNT = 6;
export function FeatureCards() {
  const $ = _c(21);
  const posthog = usePostHog();
  const {
    locale,
    t
  } = useDocsI18n();
  const [expanded, setExpanded] = useState(false);
  let remaining;
  let t0;
  let t1;
  let t2;
  if ($[0] !== expanded || $[1] !== locale || $[2] !== posthog) {
    const visible = expanded ? features : features.slice(0, INITIAL_COUNT);
    remaining = features.length - INITIAL_COUNT;
    t2 = "not-prose";
    t0 = "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3";
    let t3;
    if ($[7] !== locale || $[8] !== posthog) {
      t3 = feature => <Link key={feature.href} href={feature.href} onClick={() => {
        posthog.capture("docs_feature_card_click", {
          feature: feature.title.en,
          href: feature.href
        });
      }} className="group relative flex flex-col gap-3 rounded-xl border border-fd-border bg-fd-card p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-fd-primary/40"><div className="flex items-center justify-between"><div className="flex items-center gap-3"><div className="rounded-lg bg-fd-primary/10 p-2 text-fd-primary transition-colors duration-200 group-hover:bg-fd-primary/20">{feature.icon}</div><h3 className="text-sm font-semibold tracking-tight text-fd-foreground">{feature.title[locale]}</h3></div><ArrowRight className="size-4 -translate-x-1 text-fd-muted-foreground opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100 group-hover:text-fd-primary" /></div><p className="text-[13px] leading-relaxed text-fd-muted-foreground">{feature.description[locale]}</p></Link>;
      $[7] = locale;
      $[8] = posthog;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    t1 = visible.map(t3);
    $[0] = expanded;
    $[1] = locale;
    $[2] = posthog;
    $[3] = remaining;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    remaining = $[3];
    t0 = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  let t3;
  if ($[10] !== t0 || $[11] !== t1) {
    t3 = <div className={t0}>{t1}</div>;
    $[10] = t0;
    $[11] = t1;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== expanded || $[14] !== remaining || $[15] !== t) {
    t4 = !expanded && <button type="button" onClick={() => setExpanded(true)} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-fd-border bg-fd-card px-4 py-2.5 text-sm font-medium text-fd-muted-foreground transition-colors duration-200 hover:bg-fd-accent hover:text-fd-foreground">{t("features.showMore", {
        count: remaining
      })}<ChevronDown className="size-4" /></button>;
    $[13] = expanded;
    $[14] = remaining;
    $[15] = t;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t2 || $[18] !== t3 || $[19] !== t4) {
    t5 = <div className={t2}>{t3}{t4}</div>;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
}